/**
 * HighlightOverlay component for SVG-based highlighting
 * Renders highlights over PDF canvas with coordinate transformations
 * Requirements: 3.1, 3.3, 3.4, 3.5
 */

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useHighlightingContext } from '@/contexts/HighlightingContext';
import { HighlightRegion } from '@/types';
import { focusFieldInput, scrollBehavior } from '@/utils/fieldFocus';

interface HighlightOverlayProps {
  pageNumber: number;
  containerWidth?: number;
  containerHeight?: number;
  className?: string;
}

interface HighlightRectProps {
  region: HighlightRegion;
  isActive: boolean;
  onSelect: (fieldId: string) => void;
}

const HighlightRect: React.FC<HighlightRectProps> = ({ region, isActive, onSelect }) => {
  const { screenCoordinates, style } = region;

  // Enhanced styling for active vs inactive highlights
  const rectStyle = useMemo(() => {
    // clickable even though the overlay itself passes clicks through (pointerEvents: none)
    const interactive = {
      transition: 'all 0.2s ease-in-out',
      cursor: 'pointer',
      pointerEvents: 'auto' as const,
    };
    if (isActive) {
      return {
        ...interactive,
        fill: '#2196F3', // Bright blue for active field
        stroke: '#1976D2', // Darker blue border
        strokeWidth: 3,
        opacity: 0.4,
        filter: 'url(#active-highlight-glow)',
      };
    }
    return {
      ...interactive,
      fill: style.backgroundColor,
      stroke: style.borderColor,
      strokeWidth: 1,
      opacity: style.opacity * 0.6, // More opaque for better visibility
    };
  }, [style, isActive]);

  return (
    <rect
      x={screenCoordinates.x}
      y={screenCoordinates.y}
      width={screenCoordinates.width}
      height={screenCoordinates.height}
      style={rectStyle}
      rx={2} // Rounded corners
      ry={2}
      onClick={() => onSelect(region.fieldId)}
    >
      {/* Hover affordance for the document→field direction (UX4) */}
      <title>Go to field</title>
    </rect>
  );
};

/**
 * Document→field navigation (UX4): clicking a highlight region moves the analyst to the matching
 * field. Editable fields get real input focus (which re-triggers the field→PDF highlight);
 * read-only fields scroll into view; if neither anchor exists, just activate the highlight.
 */
function useSelectFieldFromDocument() {
  const { actions } = useHighlightingContext();

  return (fieldId: string) => {
    // Editable field: real input focus (re-triggers the field→PDF highlight).
    if (focusFieldInput(fieldId)) return;

    // Read-only field: scroll into view and activate the highlight directly.
    const readOnlyAnchor = document.getElementById(`readonly-field-${fieldId}`);
    if (readOnlyAnchor) {
      readOnlyAnchor.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    }
    actions.setActiveField(fieldId);
  };
}

const HighlightOverlay: React.FC<HighlightOverlayProps> = ({
  pageNumber,
  containerWidth = 800,
  containerHeight = 600,
  className
}) => {
  const { state } = useHighlightingContext();
  const selectField = useSelectFieldFromDocument();

  // Filter highlights for current page
  const pageHighlights = useMemo(() => {
    return state.highlightedRegions.filter(region => region.page === pageNumber);
  }, [state.highlightedRegions, pageNumber]);

  // Calculate SVG dimensions based on PDF viewport
  const svgDimensions = useMemo(() => {
    if (!state.pdfViewport) {
      return { width: containerWidth, height: containerHeight };
    }
    
    return {
      width: state.pdfViewport.width,
      height: state.pdfViewport.height
    };
  }, [state.pdfViewport, containerWidth, containerHeight]);

  // Don't render if no highlights or no viewport
  if (pageHighlights.length === 0 || !state.pdfViewport) {
    return null;
  }

  return (
    <Box
      className={className}
      sx={{
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: svgDimensions.width,
        height: svgDimensions.height,
        pointerEvents: 'none', // Allow clicks to pass through to PDF
        zIndex: 10, // Ensure highlights appear above PDF
      }}
    >
      <svg
        width={svgDimensions.width}
        height={svgDimensions.height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
        viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Render highlight rectangles */}
        {pageHighlights.map((region, index) => (
          <HighlightRect
            key={`${region.fieldId}-${region.page}-${index}`}
            region={region}
            isActive={region.fieldId === state.activeFieldId}
            onSelect={selectField}
          />
        ))}
        
        {/* Enhanced filters for better visibility */}
        <defs>
          <filter id="highlight-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="1" dy="1" stdDeviation="1" floodOpacity="0.3"/>
          </filter>
          <filter id="active-highlight-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
      </svg>
    </Box>
  );
};

export default HighlightOverlay;